import { useEffect, useRef } from '@wordpress/element';

import loadImage from './utils/loadImage';
import renderImageToCanvas from './utils/renderImageToCanvas';

/**
 *
 * A canvas component that renders an image with a crop applied
 *
 * @param {Object}  props             Component props
 * @param {number}  props.aspectRatio Image aspect ratio
 * @param {string}  props.url         Image URL
 * @param {Crop}    props.crop        Image crop
 * @param {boolean} props.isCircular  Is the image circular
 *
 * @return {JSX.Element}
 */
const ImageCanvas = ({ aspectRatio, url, crop, isCircular = false }) => {
  const canvasRef = useRef(null);

  /**
   * Loads a remote image whenever the URL changes, and attempts to render it to the canvas
   */
  useEffect(() => {
    /**
     * Load an image from a URL
     *
     * @param {string} src Image URL
     *
     * @return {Promise<void>}
     */
    const loadImageFromUrl = async (src) => {
      try {
        const image = await loadImage(src);
        if (image && canvasRef.current) {
          renderImageToCanvas(image, canvasRef.current, aspectRatio, crop, isCircular);
        }
      } catch (e) {
        // eslint-disable-next-line no-console -- Log the error for debugging
        console.error(e);
      }
    };

    loadImageFromUrl(url);
  }, [url, aspectRatio, crop, isCircular]);

  return <canvas ref={canvasRef} style={{ maxWidth: '100%' }} />;
};

export default ImageCanvas;
